/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, IconButton, Paper, Snackbar } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { useTranslation } from 'react-i18next';

import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import ItemRecipes from '../../Components/ItemRecipes/ItemRecipes';

import { inventoryAPI, productionMethodAPI } from '../../services/apiService';

import FormulationDialog from '../../Components/FormulationDialog/FormulationDialog';

import './Formulation.css';

function Formulation() {
    const { t, i18n } = useTranslation();

    const [inventories, setInventories] = useState([]);
    const [formulations, setFormulations] = useState([]);

    const [loading, setLoading] = useState(true);

    const [openDialog, setOpenDialog] = useState(false);
    const [editingFormulation, setEditingFormulation] = useState(null);
    const [activeSection, setActiveSection] = useState(0);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            const [inventoryResult, formulationResult] = await Promise.all([
                inventoryAPI.getAll({
                    force: true,
                    fallback: [],
                }),

                productionMethodAPI.getAll({
                    force: true,
                    fallback: [],
                }),
            ]);

            setInventories(Array.isArray(inventoryResult) ? inventoryResult : []);

            setFormulations(Array.isArray(formulationResult) ? formulationResult : []);
        } catch (error) {
            console.error('Failed to fetch formulation data:', error);

            showSnackbar(error?.message || t('formulation_fetch_error'), 'error');
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenCreate = () => {
        setEditingFormulation(null);
        setOpenDialog(true);
    };

    const handleOpenEdit = (formulation) => {
        setEditingFormulation(formulation);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingFormulation(null);
    };

    const handleSaved = async (result) => {
        if (!result?.success) {
            showSnackbar(result?.message || t('formulation_save_error'), 'error');
            return;
        }
        handleCloseDialog();
        await fetchData();
        showSnackbar(result.message || t('formulation_saved'), 'success');
    };

    const handleDelete = async (formulation) => {
        try {
            await productionMethodAPI.remove(formulation.id);

            await fetchData();

            showSnackbar(t('formulation_deleted'), 'success');
        } catch (error) {
            console.error('Failed to delete formulation:', error);

            showSnackbar(error?.message || t('formulation_delete_error'), 'error');
        }
    };

    const getIngredientName = (line) => {
        if (line?.ingredient_name) {
            return line.ingredient_name;
        }

        if (line?.ingredient?.name) {
            return line.ingredient.name;
        }

        const inventory = inventories.find(
            (item) =>
                Number(item.id) ===
                Number(
                    typeof line?.ingredient === 'object' ? line.ingredient?.id : line?.ingredient
                )
        );

        return inventory?.name || '-';
    };

    const getIngredientUnit = (line) => {
        if (line?.ingredient_unit) {
            return line.ingredient_unit;
        }

        if (line?.ingredient?.unit) {
            return line.ingredient.unit;
        }

        const inventory = inventories.find(
            (item) =>
                Number(item.id) ===
                Number(
                    typeof line?.ingredient === 'object' ? line.ingredient?.id : line?.ingredient
                )
        );

        return inventory?.unit || '';
    };
    const formatNumber = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }

        const number = Number(value);

        if (!Number.isFinite(number)) {
            return value;
        }

        return new Intl.NumberFormat(i18n.language, {
            maximumFractionDigits: 4,
        }).format(number);
    };

    const getUnitLabel = (unit) => {
        const unitKeys = {
            kg: 'unit_kg',
            g: 'unit_g',
            liter: 'unit_liter',
            ml: 'unit_ml',
            piece: 'unit_piece',
            pack: 'unit_pack',
        };
        return unitKeys[unit] ? t(unitKeys[unit]) : unit || '-';
    };

    return (
        <div className="formulation_page">
            <Sidebar />

            <div className="formulation_main">
                <Navbar />

                <main className="formulation_content">
                    <div className="formulation_header">
                        <div className="formulation_header_title">
                            <h1>{t('formulations')}</h1>

                            <p>{t('formulations_description')}</p>
                        </div>

                        {activeSection === 0 && (
                            <Button
                                variant="contained"
                                className="add_new_formulation"
                                startIcon={<AddIcon />}
                                onClick={handleOpenCreate}
                            >
                                {t('add_formulation')}
                            </Button>
                        )}
                    </div>
                    <Tabs
                        value={activeSection}
                        onChange={(event, newValue) => setActiveSection(newValue)}
                        aria-label={t('formulation_sections', 'Formulation sections')}
                        className="formulation_tabs"
                        sx={{ mb: 3 }}
                    >
                        <Tab label={t('inventory_formulations', 'Inventory formulations')} />
                        <Tab label={t('menu_item_recipes_title', 'Menu item recipes')} />
                    </Tabs>

                    {activeSection === 0 ? (
                        loading ? (
                            <div className="formulation_loading">{t('loading')}</div>
                        ) : formulations.length === 0 ? (
                            <Paper className="formulation_empty">
                                <Inventory2Icon className="formulation_empty_icon" />

                                <h2>{t('no_formulations')}</h2>

                                <p>{t('no_formulations_description')}</p>

                                <Button
                                    variant="contained"
                                    className="add_new_formulation"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenCreate}
                                >
                                    {t('add_formulation')}
                                </Button>
                            </Paper>
                        ) : (
                            <div className="formulations_grid">
                                {formulations.map((formulation) => (
                                    <Paper key={formulation.id} className="formulation_card">
                                        <div>
                                            <div className="formulation_card_header">
                                                <div>
                                                    <h2>{formulation.name}</h2>

                                                    <span className="formulation_output">
                                                        {t('output')}:{' '}
                                                        <strong>{formulation.output_name}</strong>
                                                    </span>
                                                </div>

                                                <div className="formulation_card_actions">
                                                    <IconButton
                                                        onClick={() => handleOpenEdit(formulation)}
                                                        aria-label={t('edit')}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>

                                                    <IconButton
                                                        onClick={() => handleDelete(formulation)}
                                                        aria-label={t('delete')}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </div>
                                            </div>

                                            <div className="formulation_output_info">
                                                <div className="formulation_info_item">
                                                    <span>{t('output_unit')}</span>

                                                    <strong>
                                                        {getUnitLabel(formulation.output_unit)}
                                                    </strong>
                                                </div>

                                                <div className="formulation_info_item">
                                                    <span>{t('output_unit_value')}</span>

                                                    <strong>
                                                        {formatNumber(
                                                            formulation.output_unit_value
                                                        )}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="formulation_ingredients">
                                            <div className="formulation_ingredients_title">
                                                <Inventory2Icon />

                                                <span>{t('ingredients')}</span>
                                            </div>

                                            {Array.isArray(formulation.ingredient_lines) &&
                                            formulation.ingredient_lines.length > 0 ? (
                                                <div className="formulation_ingredient_list">
                                                    {formulation.ingredient_lines.map((line) => (
                                                        <div
                                                            className="formulation_ingredient"
                                                            key={line.id}
                                                        >
                                                            <span>{getIngredientName(line)}</span>

                                                            <strong>
                                                                {formatNumber(line.quantity)}
                                                                {getUnitLabel(
                                                                    getIngredientUnit(line)
                                                                )}
                                                            </strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="formulation_no_ingredients">
                                                    {t('no_ingredients')}
                                                </div>
                                            )}
                                        </div>
                                    </Paper>
                                ))}
                            </div>
                        )
                    ) : (
                        <ItemRecipes />
                    )}
                </main>
            </div>

            <FormulationDialog
                open={openDialog}
                onClose={handleCloseDialog}
                inventories={inventories}
                editingFormulation={editingFormulation}
                onSaved={handleSaved}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() =>
                    setSnackbar((prev) => ({
                        ...prev,
                        open: false,
                    }))
                }
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() =>
                        setSnackbar((prev) => ({
                            ...prev,
                            open: false,
                        }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Formulation;
